import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { X, ChevronRight, ChevronLeft, Sparkles } from 'lucide-react';
import { safeStorage } from '@/lib/safeStorage';

export interface TourStep {
  /** CSS selector or data-testid to highlight */
  target: string;
  /** i18n key del título (o texto literal — si la clave no existe se muestra tal cual) */
  title: string;
  /** i18n key de la descripción (mismo fallback que title) */
  content: string;
  /** Preferred placement */
  placement?: 'top' | 'bottom' | 'left' | 'right';
}

interface GuidedTourProps {
  steps: TourStep[];
  tourId: string;
  onComplete?: () => void;
  onSkip?: () => void;
}

const STORAGE_PREFIX = 'verix_tour_';

export function useTour(tourId: string) {
  const key = STORAGE_PREFIX + tourId;
  const [completed, setCompleted] = useState(() => {
    try { return safeStorage.getItem(key) === 'done'; } catch { return false; }
  });

  const markComplete = useCallback(() => {
    try { safeStorage.setItem(key, 'done'); } catch {}
    setCompleted(true);
  }, [key]);

  const reset = useCallback(() => {
    try { safeStorage.removeItem(key); } catch {}
    setCompleted(false);
  }, [key]);

  return { completed, markComplete, reset };
}

// ¿Está en el DOM el ancla de este paso? Misma resolución que
// `calculatePosition`: primero `data-testid`, luego selector CSS.
function hasAnchor(step: TourStep) {
  if (typeof document === 'undefined') return true;
  if (document.querySelector(`[data-testid="${step.target}"]`)) return true;
  try { return !!document.querySelector(step.target); } catch { return false; }
}

export default function GuidedTour({ steps: allSteps, tourId, onComplete, onSkip }: GuidedTourProps) {
  const { t } = useTranslation();
  // Un paso cuyo ancla no está montada no se puede señalar: `calculatePosition`
  // hace `return` sin tocar la posición y el tooltip se queda sobre el recuadro
  // del paso ANTERIOR, apuntando a la nada — no falla, miente.
  //
  // No es hipotético: `ai-panel-toggle` ya no se monta en la superficie de ARIA,
  // que es justo donde aterriza el usuario cuando el tour arranca tras el
  // onboarding. Se filtra en vez de borrar el paso porque en el resto de la app
  // el botón sigue ahí y merece presentarse.
  //
  // El filtro va en un efecto, no en el initializer del `useState`: leer el DOM
  // durante el render lo ejecuta dos veces bajo StrictMode y, sobre todo, lo hace
  // ANTES del commit. Aquí se mide el DOM ya pintado. Corre una sola vez
  // (deps `[]`) a propósito: el tour no debe reordenarse bajo los pies del
  // usuario a mitad de recorrido.
  const [steps, setSteps] = useState(allSteps);
  // `[]` deliberado: `allSteps` fuera de las deps para que el filtro NO se
  // rehaga si el padre recrea el array.
  useEffect(() => {
    setSteps(allSteps.filter(hasAnchor));
  }, []);
  const [currentStep, setCurrentStep] = useState(0);
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0, height: 0 });
  const [tooltipPos, setTooltipPos] = useState({ top: 0, left: 0 });
  const [visible, setVisible] = useState(true);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const { markComplete } = useTour(tourId);

  const step = steps[currentStep];

  const calculatePosition = useCallback(() => {
    if (!step) return;
    // Try data-testid first, then CSS selector
    let el = document.querySelector(`[data-testid="${step.target}"]`);
    if (!el) el = document.querySelector(step.target);
    if (!el) return;

    const rect = el.getBoundingClientRect();
    const padding = 8;

    setPosition({
      top: rect.top - padding + window.scrollY,
      left: rect.left - padding,
      width: rect.width + padding * 2,
      height: rect.height + padding * 2,
    });

    // Calculate tooltip position based on placement
    const placement = step.placement || 'bottom';
    const tooltipWidth = 340;
    const tooltipHeight = 160;
    let tTop = 0, tLeft = 0;

    switch (placement) {
      case 'bottom':
        tTop = rect.bottom + 16 + window.scrollY;
        tLeft = Math.max(16, Math.min(rect.left, window.innerWidth - tooltipWidth - 16));
        break;
      case 'top':
        tTop = rect.top - tooltipHeight - 16 + window.scrollY;
        tLeft = Math.max(16, Math.min(rect.left, window.innerWidth - tooltipWidth - 16));
        break;
      case 'right':
        tTop = Math.max(16, Math.min(rect.top + window.scrollY, window.innerHeight - tooltipHeight - 16));
        tLeft = rect.right + 16;
        break;
      case 'left':
        tTop = Math.max(16, Math.min(rect.top + window.scrollY, window.innerHeight - tooltipHeight - 16));
        tLeft = rect.left - tooltipWidth - 16;
        break;
    }

    setTooltipPos({ top: tTop, left: tLeft });
  }, [step]);

  useEffect(() => {
    calculatePosition();
    window.addEventListener('resize', calculatePosition);
    window.addEventListener('scroll', calculatePosition);
    return () => {
      window.removeEventListener('resize', calculatePosition);
      window.removeEventListener('scroll', calculatePosition);
    };
  }, [calculatePosition]);

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      handleComplete();
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) setCurrentStep(prev => prev - 1);
  };

  const handleComplete = () => {
    markComplete();
    setVisible(false);
    onComplete?.();
  };

  const handleSkip = () => {
    markComplete();
    setVisible(false);
    onSkip?.();
  };

  if (!visible || !step) return null;

  return (
    <div className="fixed inset-0 z-toast" aria-live="polite">
      {/* Overlay with cutout */}
      <div className="absolute inset-0 bg-black/50 transition-opacity duration-300" onClick={handleSkip} />

      {/* Highlight cutout */}
      <div
        className="absolute rounded-lg ring-2 ring-primary ring-offset-2 bg-transparent z-toast transition-all duration-300 pointer-events-none"
        style={{
          top: position.top,
          left: position.left,
          width: position.width,
          height: position.height,
        }}
      />

      {/* Tooltip */}
      <div
        ref={tooltipRef}
        className="absolute z-tooltip w-[340px] bg-card rounded-xl shadow-2xl border border-border p-5 transition-all duration-300"
        style={{ top: tooltipPos.top, left: tooltipPos.left }}
        role="dialog"
        aria-label={t('tour.step_of', {
          current: currentStep + 1,
          total: steps.length,
          title: t(step.title, step.title),
          defaultValue: 'Paso {{current}} de {{total}}: {{title}}',
        })}
      >
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            {/* `t(key, key)`: si el paso trae un texto literal en vez de una
                clave (tours de terceros), se muestra tal cual. */}
            <h3 className="font-semibold text-foreground">{t(step.title, step.title)}</h3>
          </div>
          <button
            onClick={handleSkip}
            className="text-muted-foreground hover:text-foreground"
            aria-label={t('tour.close', 'Cerrar tour')}
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Content */}
        <p className="text-sm text-muted-foreground mb-4 leading-relaxed">
          {t(step.content, step.content)}
        </p>

        {/* Footer */}
        <div className="flex items-center justify-between">
          <span className="text-xs text-muted-foreground">
            {currentStep + 1} / {steps.length}
          </span>
          <div className="flex gap-2">
            {currentStep > 0 && (
              <Button variant="ghost" size="sm" onClick={handlePrev}>
                <ChevronLeft className="h-4 w-4 mr-1" />
                {t('tour.prev', 'Anterior')}
              </Button>
            )}
            <Button size="sm" onClick={handleNext}>
              {currentStep < steps.length - 1 ? (
                <>
                  {t('tour.next', 'Siguiente')}
                  <ChevronRight className="h-4 w-4 ml-1" />
                </>
              ) : (
                t('tour.finish', 'Finalizar')
              )}
            </Button>
          </div>
        </div>

        {/* Step dots */}
        <div className="flex justify-center gap-1.5 mt-3">
          {steps.map((_, i) => (
            <div
              key={i}
              className={`h-1.5 rounded-full transition-all ${
                i === currentStep ? 'w-4 bg-primary' : 'w-1.5 bg-muted'
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
